import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import FilterModule from '../../../shared/components/filters/FilterModule';
import FilterPopup from '../../../shared/components/filters/FilterPopup';
import EventPropertyFilterModule from './EventPropertyFilterModule';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import { Plus, MousePointerClick, Navigation, Scroll, FileText, Download, Upload, Eye, Zap, ChevronDown, Type, Hash, ToggleLeft, List, Box } from 'lucide-react';
import Icon from '../../../shared/components/Icon';

/**
 * Events-specific Filter Module
 * Uses the modular FilterModule with Events-specific configuration
 * Has two sections: Events and Filters
 */
const EventsFilterModule = ({
  filters = [],
  onFiltersChange,
  teamId,
  onApplyFilters
}) => {
  const [fieldOptionsCache, setFieldOptionsCache] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  const [selectedEvents, setSelectedEvents] = useState([]); // Array of { id, eventName, eventType }
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [isPropertyFilterOpen, setIsPropertyFilterOpen] = useState(false);
  const [selectedEventForPropertyFilter, setSelectedEventForPropertyFilter] = useState(null);
  const [editingPropertyFilter, setEditingPropertyFilter] = useState(null); // Track which property filter is being edited
  const [eventPropertyFilters, setEventPropertyFilters] = useState([]); // Filters for specific events
  const [globalFilters, setGlobalFilters] = useState([]); // Global filters (apply after event filters)
  const [isGlobalFilterPopupOpen, setIsGlobalFilterPopupOpen] = useState(false);
  const [editingGlobalFilter, setEditingGlobalFilter] = useState(null);
  const addButtonRef = useRef(null);
  const addGlobalFilterRef = useRef(null);
  const filterButtonRefs = useRef({}); // Store refs for each event's filter button
  const propertyFilterButtonRefs = useRef({}); // Store refs for each property filter button
  const globalFilterButtonRefs = useRef({}); // Store refs for each global filter chip (edit)

  // Initialize selected events from existing filters (only on mount)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return; // Only initialize once
    
    const eventNameFilters = filters.filter(f => (f.field === 'eventName' || f.field === 'event_name') && f.category === 'events');
    if (eventNameFilters.length > 0) {
      const events = [];
      eventNameFilters.forEach(filter => {
        if (filter.operator === 'includes' && Array.isArray(filter.value)) {
          // Handle 'includes' operator with array
          filter.value.forEach(eventName => {
            if (!events.find(e => e.id === eventName)) {
              events.push({
                id: eventName,
                eventName: eventName,
                eventType: 'custom' // Default, could be enhanced
              });
            }
          });
        } else if (filter.operator === 'equals' && filter.value) {
          // Handle 'equals' operator
          if (!events.find(e => e.id === filter.value)) {
            events.push({
              id: filter.value,
              eventName: filter.value,
              eventType: 'custom'
            });
          }
        } else if (filter.operator === 'in' && Array.isArray(filter.value)) {
          // Handle legacy 'in' operator
          filter.value.forEach(eventName => {
            if (!events.find(e => e.id === eventName)) {
              events.push({
                id: eventName,
                eventName: eventName,
                eventType: 'custom'
              });
            }
          });
        }
      });
      if (events.length > 0) {
        setSelectedEvents(events);
      }
    }
    // Initialize global filters from props (category === 'global' or 'first_time')
    const globalFromProps = filters.filter(f => f.category === 'global' || f.category === 'first_time');
    if (globalFromProps.length > 0) {
      const mapped = globalFromProps.map(f => {
        if (f.field === 'firstTime' && f.value === true) {
          return { id: f.id || `global-first-time-${Date.now()}`, category: 'first_time', connector: f.connector || 'WHERE' };
        }
        return {
          id: f.id,
          property: f.field,
          propertyLabel: f.propertyLabel || f.field,
          propertyType: f.propertyType || 'string',
          propertySource: f.propertySource || 'event',
          operator: f.operator,
          value: f.value,
          value2: f.value2,
          connector: f.connector || 'AND',
          listMode: f.listMode,
          listItemType: f.listItemType,
          category: 'global'
        };
      });
      setGlobalFilters(mapped);
    }
    hasInitialized.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Separate filters into event filters and general filters (general no longer used for global - we use globalFilters state)
  const eventFilterFields = ['event_name', 'event_type', 'event_category'];
  const eventFilters = filters.filter(f => eventFilterFields.includes(f.field));
  // General filters: exclude event_properties and global/first_time (those are in globalFilters state)
  const generalFilters = filters.filter(f =>
    !eventFilterFields.includes(f.field) &&
    f.category !== 'event_properties' &&
    f.category !== 'global' &&
    f.category !== 'first_time'
  );

  // Events-specific filter configuration (for Events section)
  const eventsFilterConfig = {
    fields: [
      { id: 'event_name', label: 'Event Name', type: 'multiselect' },
      { id: 'event_type', label: 'Event Type', type: 'multiselect', options: [
        { value: 'auto', label: 'Auto' },
        { value: 'custom', label: 'Custom' }
      ]},
      { id: 'event_category', label: 'Event Category', type: 'multiselect' }
    ],
    operators: {
      multiselect: [
        { value: 'in', label: 'is one of' },
        { value: 'not_in', label: 'is not one of' }
      ],
      text: [
        { value: 'equals', label: 'equals' },
        { value: 'contains', label: 'contains' },
        { value: 'starts_with', label: 'starts with' },
        { value: 'ends_with', label: 'ends with' },
        { value: 'not_equals', label: 'does not equal' }
      ],
      boolean: [
        { value: 'equals', label: 'is' }
      ],
      datetime: [
        { value: 'equals', label: 'equals' },
        { value: 'after', label: 'after' },
        { value: 'before', label: 'before' },
        { value: 'between', label: 'between' }
      ]
    },
    defaultOperators: {
      multiselect: 'in',
      text: 'contains',
      boolean: 'equals',
      datetime: 'between'
    }
  };

  // General filters configuration (for Filters section)
  const generalFiltersConfig = {
    fields: [
      { id: 'country', label: 'Country', type: 'multiselect' },
      { id: 'city', label: 'City', type: 'multiselect' },
      { id: 'browser', label: 'Browser', type: 'multiselect' },
      { id: 'os', label: 'Operating System', type: 'multiselect' },
      { id: 'device_type', label: 'Device Type', type: 'multiselect', options: [
        { value: 'desktop', label: 'Desktop' },
        { value: 'mobile', label: 'Mobile' },
        { value: 'tablet', label: 'Tablet' }
      ]},
      { id: 'language', label: 'Language', type: 'multiselect' },
      { id: 'url', label: 'URL', type: 'text' },
      { id: 'initial_referrer', label: 'Initial Referrer', type: 'text' },
      { id: 'element_class', label: 'Element Class', type: 'text' },
      { id: 'element_id', label: 'Element ID', type: 'text' },
      { id: 'element_tag_name', label: 'Element Tag', type: 'text' },
      { id: 'is_web3_user', label: 'Is Web3 User', type: 'boolean' },
      { id: 'has_wallet_address', label: 'Has Wallet Address', type: 'boolean' },
      { id: 'timestamp', label: 'Timestamp', type: 'datetime' }
    ],
    operators: {
      multiselect: [
        { value: 'in', label: 'is one of' },
        { value: 'not_in', label: 'is not one of' }
      ],
      text: [
        { value: 'equals', label: 'equals' },
        { value: 'contains', label: 'contains' },
        { value: 'starts_with', label: 'starts with' },
        { value: 'ends_with', label: 'ends with' },
        { value: 'not_equals', label: 'does not equal' }
      ],
      boolean: [
        { value: 'equals', label: 'is' }
      ],
      datetime: [
        { value: 'equals', label: 'equals' },
        { value: 'after', label: 'after' },
        { value: 'before', label: 'before' },
        { value: 'between', label: 'between' }
      ]
    },
    defaultOperators: {
      multiselect: 'in',
      text: 'contains',
      boolean: 'equals',
      datetime: 'between'
    }
  };

  // Fetch options for a field
  const getFieldOptions = async (fieldId) => {
    // Return cached options if available
    if (fieldOptionsCache[fieldId]) {
      return fieldOptionsCache[fieldId];
    }

    // Return static options if available
    const fieldConfig = eventsFilterConfig.fields.find(f => f.id === fieldId);
    if (fieldConfig?.options) {
      return fieldConfig.options;
    }

    // Fetch from API for dynamic fields
    if (!teamId) return [];

    try {
      setLoadingOptions(prev => ({ ...prev, [fieldId]: true }));

      let endpoint = '';
      switch (fieldId) {
        case 'event_name':
          endpoint = `/events/summaries?team_id=${teamId}`;
          break;
        case 'country':
          endpoint = `/sessions/countries?team_id=${teamId}`;
          break;
        case 'browser':
        case 'os':
        case 'language':
        case 'city':
          // These would need custom endpoints
          endpoint = null;
          break;
        default:
          endpoint = null;
      }

      if (endpoint) {
        const response = await axiosInstance.get(endpoint);
        if (response.data.success) {
          let options = [];
          
          if (fieldId === 'event_name' && response.data.summaries) {
            options = response.data.summaries.map(s => ({
              value: s.event_name,
              label: s.event_name
            }));
          } else if (response.data.countries || response.data.options) {
            const data = response.data.countries || response.data.options || [];
            options = data.map(item => ({
              value: item.value || item,
              label: item.label || item
            }));
          }

          setFieldOptionsCache(prev => ({ ...prev, [fieldId]: options }));
          return options;
        }
      }
    } catch (error) {
      console.error(`Error fetching options for ${fieldId}:`, error);
    } finally {
      setLoadingOptions(prev => ({ ...prev, [fieldId]: false }));
    }

    return [];
  };

  // Fetch available events
  useEffect(() => {
    if (teamId && isPopupOpen) {
      fetchAvailableEvents();
    }
  }, [teamId, isPopupOpen]);

  const fetchAvailableEvents = async () => {
    if (!teamId) {
      console.warn('No teamId provided for fetching events');
      setAvailableEvents([]);
      return;
    }
    
    setLoadingEvents(true);
    try {
      // Handle different teamId formats:
      // 1. Object with _id property (from useTeam hook)
      // 2. String from localStorage.getItem('selectedTeam') - need to get ID from selectedTeamData
      // 3. Number (direct ID)
      let numericTeamId = null;
      
      if (typeof teamId === 'object' && teamId !== null) {
        // Object format - extract _id or id
        numericTeamId = parseInt(teamId._id || teamId.id, 10);
      } else if (typeof teamId === 'string') {
        // String format - might be team name, try to get ID from selectedTeamData
        try {
          const teamDataStr = localStorage.getItem('selectedTeamData');
          if (teamDataStr) {
            const teamData = JSON.parse(teamDataStr);
            numericTeamId = parseInt(teamData._id || teamData.id, 10);
          } else {
            // Fallback: try parsing as number
            numericTeamId = parseInt(teamId, 10);
          }
        } catch (e) {
          // If parsing fails, try direct conversion
          numericTeamId = parseInt(teamId, 10);
        }
      } else {
        // Number format
        numericTeamId = parseInt(teamId, 10);
      }
      
      if (isNaN(numericTeamId) || !numericTeamId) {
        console.error('Invalid teamId format:', teamId);
        setAvailableEvents([]);
        return;
      }

      const response = await axiosInstance.get('/events/summaries', {
        params: {
          team_id: numericTeamId
        }
      });

      if (response.data && response.data.success) {
        const summaries = response.data.summaries || [];
        const seen = new Set();
        const events = summaries
          .filter(s => {
            const key = `${s.event_name}-${s.event_type || 'custom'}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map((s, idx) => ({
            id: `${s.event_name}-${s.event_type || 'custom'}-${idx}`,
            eventName: s.event_name,
            eventType: s.event_type || 'custom',
            count: s.count || 0
          }));
        setAvailableEvents(events);
      } else {
        console.warn('API response missing success flag or summaries:', response.data);
        setAvailableEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      console.error('Error details:', error.response?.data || error.message);
      setAvailableEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  // Update filters when selected events change
  useEffect(() => {
    // Skip if we haven't initialized yet (to avoid conflicts with initialization)
    if (!hasInitialized.current) {
      return;
    }
    
    console.log('Filter update triggered with selectedEvents:', selectedEvents);
    
    // Create event_name filters for selected events
    // Note: Backend expects 'eventName' field name and 'includes' operator
    let eventNameFilters = [];
    if (selectedEvents.length > 0) {
      // Use 'includes' operator which backend supports (works for both single and multiple events)
      const eventNames = selectedEvents.map(e => e.eventName);
      console.log('Creating filter with event names:', eventNames);
      eventNameFilters = [{
        id: `event-filter-${Date.now()}`, // Unique ID to avoid duplicates
        field: 'eventName', // Backend fieldMap expects 'eventName'
        operator: 'includes', // Backend supports 'includes' operator for multiselect
        value: eventNames, // Always use array, backend handles it
        category: 'events'
      }];
      console.log('Event filter created:', { field: 'eventName', operator: 'includes', value: eventNames, count: eventNames.length });
    }
    
    // Convert event property filters to the format expected by the main filter system
    // Include eventName and eventId so backend can group filters by event
    const convertedEventPropertyFilters = eventPropertyFilters.map(filter => {
      // First time filter: pass through as-is (no operator/value in UI, fixed filter)
      if (filter.category === 'first_time') {
        return {
          id: filter.id,
          field: 'firstTime',
          operator: 'equals',
          value: true,
          category: 'first_time'
        };
      }
      // Ensure value is an array for 'is' and 'is_not' operators with multiselect
      let filterValue = filter.value;
      if ((filter.operator === 'is' || filter.operator === 'is_not') && filter.propertyType === 'string') {
        // Ensure it's always an array for multiselect
        if (!Array.isArray(filterValue)) {
          filterValue = filterValue ? [filterValue] : [];
        }
      }
      return {
        id: filter.id,
        field: filter.property,
        operator: filter.operator,
        value: filterValue,
        value2: filter.value2,
        connector: filter.connector,
        propertyType: filter.propertyType,
        propertySource: filter.propertySource,
        category: 'event_properties',
        eventName: filter.eventName, // Include event name for grouping
        eventId: filter.eventId, // Include event ID for grouping
        listMode: filter.listMode, // Preserve list mode for list types
        listItemType: filter.listItemType // Preserve list item type for list types
      };
    });

    // Combine with general filters (preserve existing general filters that are not event filters)
    // Filter out any existing eventName filters and event property filters to avoid duplicates
    // Also filter out any filters that match the IDs of convertedEventPropertyFilters
    // IMPORTANT: Don't use filters prop for event property filters - only use our internal eventPropertyFilters state
    const convertedFilterIds = new Set(convertedEventPropertyFilters.map(f => f.id));
    const eventPropertyFilterIds = new Set(eventPropertyFilters.map(f => f.id));
    const currentGeneralFilters = filters.filter(f => {
      if (f.field === 'eventName' || f.field === 'event_name') return false;
      if (f.category === 'first_time') return false;
      if (f.category === 'global') return false;
      if (f.category === 'event_properties' || convertedFilterIds.has(f.id) || eventPropertyFilterIds.has(f.id)) return false;
      return true;
    });

    // Convert global filters to API format (WHERE/AND stacking, category: 'global' or firstTime)
    const convertedGlobalFilters = globalFilters.map(f => {
      if (f.category === 'first_time') {
        return { id: f.id, field: 'firstTime', operator: 'equals', value: true, category: 'first_time', connector: f.connector };
      }
      let filterValue = f.value;
      if ((f.operator === 'is' || f.operator === 'is_not') && f.propertyType === 'string') {
        filterValue = Array.isArray(filterValue) ? filterValue : (filterValue ? [filterValue] : []);
      }
      return {
        id: f.id,
        field: f.property,
        operator: f.operator,
        value: filterValue,
        value2: f.value2,
        connector: f.connector,
        propertyType: f.propertyType,
        propertySource: f.propertySource,
        category: 'global',
        listMode: f.listMode,
        listItemType: f.listItemType
      };
    });

    const allFilters = [...eventNameFilters, ...convertedEventPropertyFilters, ...convertedGlobalFilters, ...currentGeneralFilters];
    console.log('Calling onFiltersChange with:', allFilters, 'Total filters:', allFilters.length);
    onFiltersChange(allFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvents, eventPropertyFilters, globalFilters]);

  const handleAddEvent = () => {
    setIsPopupOpen(true);
  };

  const handleEventSelect = (event) => {
    // Check if event is already selected
    if (!selectedEvents.find(e => e.id === event.id)) {
      // Use functional update to ensure we have the latest state
      setSelectedEvents(prev => {
        const updated = [...prev, event];
        console.log('Selected events updated:', updated);
        return updated;
      });
    }
    setIsPopupOpen(false);
  };

  const handleDeleteEvent = (eventId) => {
    // Find the event being deleted to get its eventName
    const eventToDelete = selectedEvents.find(e => e.id === eventId);
    const eventNameToDelete = eventToDelete?.eventName;
    
    // Use functional update to ensure we have the latest state
    setSelectedEvents(prev => {
      const updated = prev.filter(e => e.id !== eventId);
      console.log('Event deleted, remaining events:', updated);
      return updated;
    });
    
    // Also remove all property filters associated with this event
    if (eventNameToDelete) {
      setEventPropertyFilters(prev => {
        const filtered = prev.filter(f => {
          // Remove filters that are associated with the deleted event
          // Check if filter has eventName or eventId property, or if it was created for this event
          return f.eventName !== eventNameToDelete && f.eventId !== eventId;
        });
        
        // Group filters by event and ensure each event's first filter has "WHERE"
        const eventGroups = {};
        filtered.forEach(filter => {
          if (!eventGroups[filter.eventName]) {
            eventGroups[filter.eventName] = [];
          }
          eventGroups[filter.eventName].push(filter);
        });
        
        // Update connectors: first filter of each event should be "WHERE", others "AND"
        Object.values(eventGroups).forEach(eventFilters => {
          eventFilters.forEach((filter, index) => {
            filter.connector = index === 0 ? 'WHERE' : 'AND';
          });
        });
        
        return filtered;
      });
    }
  };

  const handleEventFilters = (eventId) => {
    const event = selectedEvents.find(e => e.id === eventId);
    if (event) {
      setSelectedEventForPropertyFilter(event);
      setIsPropertyFilterOpen(true);
    }
  };

  const handleAddGlobalFilter = () => {
    setEditingGlobalFilter(null);
    setIsGlobalFilterPopupOpen(true);
  };

  const handleGlobalFilterAdd = (filter) => {
    setGlobalFilters(prev => {
      const existing = prev.find(f => f.id === filter.id);
      if (existing) {
        return prev.map(f => f.id === filter.id ? { ...filter, connector: f.connector } : f);
      }
      const connector = prev.length === 0 ? 'WHERE' : 'AND';
      return [...prev, { ...filter, connector }];
    });
    setIsGlobalFilterPopupOpen(false);
    setEditingGlobalFilter(null);
  };

  const handleGlobalFilterUpdate = (filterId, updates) => {
    setGlobalFilters(prev => prev.map(f => f.id === filterId ? { ...f, ...updates } : f));
  };

  const handleGlobalFilterRemove = (filterId) => {
    setGlobalFilters(prev => {
      const filtered = prev.filter(f => f.id !== filterId);
      return filtered.map((f, i) => ({ ...f, connector: i === 0 ? 'WHERE' : 'AND' }));
    });
    if (editingGlobalFilter?.id === filterId) {
      setEditingGlobalFilter(null);
      setIsGlobalFilterPopupOpen(false);
    }
  };

  const handleEventPropertyFilterUpdate = (filterId, updates) => {
    setEventPropertyFilters(prev => {
      const updated = prev.map(f => {
        if (f.id === filterId) {
          // Preserve eventName and eventId when updating
          // Ensure value is preserved as array for 'is' and 'is_not' operators with string properties
          let updatedValue = updates.value;
          if (updates.value !== undefined && (f.operator === 'is' || f.operator === 'is_not') && f.propertyType === 'string') {
            // Ensure it's always an array for multiselect
            if (!Array.isArray(updatedValue)) {
              updatedValue = updatedValue ? [updatedValue] : [];
            }
          }
          const newFilter = { 
            ...f, 
            ...updates, 
            ...(updates.value !== undefined ? { value: updatedValue } : {}),
            eventName: f.eventName, 
            eventId: f.eventId 
          };
          return newFilter;
        }
        return f;
      });
      return updated;
    });
  };

  const handleEventPropertyFilterRemove = (filterId) => {
    setEventPropertyFilters(prev => {
      const filtered = prev.filter(f => f.id !== filterId);
      const eventGroups = {};
      filtered.forEach(filter => {
        const key = filter.eventName ?? '__global__';
        if (!eventGroups[key]) eventGroups[key] = [];
        eventGroups[key].push(filter);
      });
      return filtered.map(filter => {
        const group = eventGroups[filter.eventName ?? '__global__'];
        const index = group.indexOf(filter);
        return { ...filter, connector: index === 0 ? 'WHERE' : 'AND' };
      });
    });
  };

  const handlePropertyFilterAdd = (propertyFilter) => {
    // First time filter: global, no eventName/eventId
    if (propertyFilter.category === 'first_time') {
      setEventPropertyFilters(prev => [...prev.filter(f => f.category !== 'first_time'), { ...propertyFilter, id: propertyFilter.id || 'first-time-filter' }]);
      return;
    }
    // Use event information from the filter itself (passed from EventPropertyFilterModule)
    const eventName = propertyFilter.eventName;
    const eventId = propertyFilter.eventId;
    
    // Check if this is the first filter for this specific event
    const existingFiltersForEvent = eventPropertyFilters.filter(f => f.eventName === eventName);
    const isFirstFilterForEvent = existingFiltersForEvent.length === 0;
    
    const newFilter = {
      ...propertyFilter,
      connector: isFirstFilterForEvent ? 'WHERE' : 'AND',
      eventName: eventName,
      eventId: eventId
    };
    setEventPropertyFilters(prev => [...prev, newFilter]);
  };

  const handleFirstTimeChange = (enabled, eventName, eventId) => {
    if (enabled) {
      setEventPropertyFilters(prev => {
        const withoutFirstTime = prev.filter(f => f.category !== 'first_time');
        // Use provided eventName/eventId or fall back to first selected event so the chip always shows somewhere
        const name = eventName ?? selectedEvents[0]?.eventName;
        const id = eventId ?? selectedEvents[0]?.id;
        const existingForEvent = withoutFirstTime.filter(f => f.eventName === name);
        const connector = existingForEvent.length === 0 ? 'WHERE' : 'AND';
        return [...withoutFirstTime, { id: 'first-time-filter', field: 'firstTime', value: true, category: 'first_time', eventName: name, eventId: id, connector }];
      });
    } else {
      setEventPropertyFilters(prev => prev.filter(f => f.category !== 'first_time'));
    }
  };

  // Get icon for event based on event name and type (same logic as FilterPopup)
  const getEventIcon = (eventName, eventType) => {
    if (eventType === 'custom') {
      return MousePointerClick;
    }

    // Map common auto event names to icons
    const eventNameLower = eventName?.toLowerCase() || '';
    
    if (eventNameLower.includes('page_view') || eventNameLower.includes('pageview')) {
      return Navigation;
    }
    if (eventNameLower.includes('click')) {
      return MousePointerClick;
    }
    if (eventNameLower.includes('scroll')) {
      return Scroll;
    }
    if (eventNameLower.includes('form') || eventNameLower.includes('submit')) {
      return FileText;
    }
    if (eventNameLower.includes('download')) {
      return Download;
    }
    if (eventNameLower.includes('upload')) {
      return Upload;
    }
    if (eventNameLower.includes('view') || eventNameLower.includes('impression')) {
      return Eye;
    }
    
    // Default icon for other auto events
    return Zap;
  };

  const handleAddGeneralFilter = () => {
    const newFilter = {
      id: `filter-${Date.now()}-${Math.random()}`,
      field: '',
      operator: '',
      value: null,
      value2: null
    };
    onFiltersChange([...filters, newFilter]);
  };

  const handleEventFiltersChange = (updatedEventFilters) => {
    // Combine updated event filters with general filters
    const allFilters = [...updatedEventFilters, ...generalFilters];
    onFiltersChange(allFilters);
  };

  const handleGeneralFiltersChange = (updatedGeneralFilters) => {
    // Combine event filters with updated general filters
    const allFilters = [...eventFilters, ...updatedGeneralFilters];
    onFiltersChange(allFilters);
  };

  // FilterPopup configuration for event selection
  const eventNavItems = [
    { id: 'all', label: 'All' }
  ];

  const eventFilterConfigs = {
    all: {
      fields: [], // We'll show events list instead
      operators: {},
      defaultOperators: {}
    }
  };

  return (
    <div className="bg-transparent border border-dotted border-gray-300 rounded-lg overflow-hidden" style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Events Section */}
      <div className="bg-transparent overflow-hidden">
        {/* Header */}
        <div className="pl-4 pr-6 pt-2 pb-3 flex items-center justify-between border-b border-dotted border-gray-300">
          <h3 className="text-xs font-bold uppercase tracking-wide text-[#0f172a]">
            EVENTS
          </h3>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Fallback: show First time only at top when it exists but no selected event matches (e.g. event was removed) */}
          {(() => {
            const firstTimeFilter = eventPropertyFilters.find(f => f.category === 'first_time');
            const hasMatch = firstTimeFilter && selectedEvents.some(e => e.eventName === firstTimeFilter.eventName);
            if (firstTimeFilter && !hasMatch) {
              const connector = firstTimeFilter.connector || 'WHERE';
              return (
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-medium text-[#475569] uppercase px-1.5">{connector}</span>
                  <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg border border-gray-200">
                    <span className="text-sm text-[#0f172a] font-medium">First time only</span>
                    <button
                      type="button"
                      onClick={() => handleEventPropertyFilterRemove(firstTimeFilter.id)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors text-[#475569] hover:text-[#0f172a]"
                      title="Remove First time only"
                    >
                      <Icon name="delete" size={16} />
                    </button>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Selected Events with their filters stacked underneath */}
          {selectedEvents.length > 0 && (
            <div className="space-y-4 mb-4">
              {selectedEvents.map((event, index) => {
                const EventIcon = getEventIcon(event.eventName, event.eventType);
                // Create or get ref for this event's filter button
                if (!filterButtonRefs.current[event.id]) {
                  filterButtonRefs.current[event.id] = React.createRef();
                }
                
                // Get filters for this specific event (include first_time when eventName matches, or when first_time has no eventName show under first event)
                const eventFilters = eventPropertyFilters.filter(f => {
                  if (f.eventName === event.eventName) return true;
                  if (f.category === 'first_time' && !f.eventName && index === 0) return true;
                  return false;
                });
                
                return (
                  <div key={`${event.id}-${index}`} className="space-y-2">
                    {/* Event Row */}
                    <div className="flex items-center gap-2">
                      <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg">
                        <EventIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />
                        <span className="text-sm text-[#0f172a] font-medium">{event.eventName}</span>
                      </div>
                      <button
                        ref={filterButtonRefs.current[event.id]}
                        onClick={() => handleEventFilters(event.id)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Filters"
                      >
                        <Icon name="filter" size={18} color="#475569" />
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(event.id)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="Delete"
                      >
                        <Icon name="delete" size={16} color="#475569" />
                      </button>
                    </div>
                    
                    {/* Filters for this event - Always visible, never collapsed (includes First time only inline) */}
                    {eventFilters.length > 0 && (
                      <div className="ml-6 space-y-2">
                        {eventFilters.map((filter, filterIndex) => {
                          const connector = filter.connector || (filterIndex === 0 ? 'WHERE' : 'AND');
                          if (filter.category === 'first_time') {
                            return (
                              <div key={filter.id} className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-medium text-[#475569] uppercase px-1.5">{connector}</span>
                                <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg border border-gray-200">
                                  <span className="text-sm text-[#0f172a] font-medium">First time only</span>
                                  <button
                                    type="button"
                                    onClick={() => handleEventPropertyFilterRemove(filter.id)}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors text-[#475569] hover:text-[#0f172a]"
                                    title="Remove First time only"
                                  >
                                    <Icon name="delete" size={16} />
                                  </button>
                                </div>
                              </div>
                            );
                          }
                          if (!propertyFilterButtonRefs.current[filter.id]) {
                            propertyFilterButtonRefs.current[filter.id] = React.createRef();
                          }
                          return (
                            <EventPropertyFilterChip
                              key={filter.id}
                              filter={filter}
                              isFirst={filterIndex === 0}
                              filterRef={propertyFilterButtonRefs.current[filter.id]}
                              onEdit={() => {
                                setEditingPropertyFilter(filter);
                                setIsPropertyFilterOpen(true);
                              }}
                              onUpdate={(updates) => handleEventPropertyFilterUpdate(filter.id, updates)}
                              onRemove={() => handleEventPropertyFilterRemove(filter.id)}
                              teamId={teamId}
                              eventName={event.eventName}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Event Button */}
          <div>
            <button
              ref={addButtonRef}
              onClick={handleAddEvent}
              className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>
      </div>

      {/* Event Selection Popup */}
      <FilterPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        triggerRef={addButtonRef}
        navItems={eventNavItems}
        filterConfigs={eventFilterConfigs}
        filters={[]}
        onFiltersChange={() => {}}
        getFieldOptions={() => []}
        onEventSelect={handleEventSelect}
        availableEvents={availableEvents}
        loadingEvents={loadingEvents}
      />

      {/* Divider */}
      <div className="border-t border-dotted border-gray-300"></div>

      {/* Global Filters Section (WHERE/AND stacking, same as event filters) */}
      <div className="bg-transparent overflow-hidden">
        <div className="pl-4 pr-6 pt-2 pb-3 flex items-center justify-between border-b border-dotted border-gray-300">
          <h3 className="text-xs font-bold uppercase tracking-wide text-[#0f172a]">
            FILTERS
          </h3>
        </div>
        <div className="p-6">
          {globalFilters.length > 0 && (
            <div className="space-y-2 mb-4">
              {globalFilters.map((filter, index) => {
                const connector = filter.connector || (index === 0 ? 'WHERE' : 'AND');
                if (filter.category === 'first_time') {
                  return (
                    <div key={filter.id} className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-[#475569] uppercase px-1.5">{connector}</span>
                      <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg border border-gray-200">
                        <span className="text-sm text-[#0f172a] font-medium">First time only</span>
                        <button
                          type="button"
                          onClick={() => handleGlobalFilterRemove(filter.id)}
                          className="p-1 hover:bg-gray-200 rounded transition-colors text-[#475569] hover:text-[#0f172a]"
                          title="Remove First time only"
                        >
                          <Icon name="delete" size={16} />
                        </button>
                      </div>
                    </div>
                  );
                }
                if (!globalFilterButtonRefs.current[filter.id]) {
                  globalFilterButtonRefs.current[filter.id] = React.createRef();
                }
                return (
                  <EventPropertyFilterChip
                    key={filter.id}
                    filter={filter}
                    isFirst={index === 0}
                    filterRef={globalFilterButtonRefs.current[filter.id]}
                    onEdit={() => {
                      setEditingGlobalFilter(filter);
                      setIsGlobalFilterPopupOpen(true);
                    }}
                    onUpdate={(updates) => handleGlobalFilterUpdate(filter.id, updates)}
                    onRemove={() => handleGlobalFilterRemove(filter.id)}
                    teamId={teamId}
                    eventName={null}
                  />
                );
              })}
            </div>
          )}
          <div>
            <button
              ref={addGlobalFilterRef}
              onClick={handleAddGlobalFilter}
              className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>
      </div>

      {/* Event Property Filter Module */}
      {(selectedEventForPropertyFilter || editingPropertyFilter) && (
        <EventPropertyFilterModule
          isOpen={isPropertyFilterOpen}
          onClose={() => {
            setIsPropertyFilterOpen(false);
            setSelectedEventForPropertyFilter(null);
            setEditingPropertyFilter(null);
          }}
          triggerRef={
            editingPropertyFilter
              ? propertyFilterButtonRefs.current[editingPropertyFilter.id]
              : filterButtonRefs.current[selectedEventForPropertyFilter?.id]
          }
          eventName={editingPropertyFilter ? (selectedEvents.length > 0 ? selectedEvents[0].eventName : null) : selectedEventForPropertyFilter?.eventName}
          eventId={editingPropertyFilter ? (selectedEvents.length > 0 ? selectedEvents[0].id : null) : selectedEventForPropertyFilter?.id}
          teamId={teamId}
          filters={filters}
          onFiltersChange={editingPropertyFilter ? (newFilter) => {
            const updatedFilter = {
              ...newFilter,
              eventName: editingPropertyFilter.eventName,
              eventId: editingPropertyFilter.eventId
            };
            handleEventPropertyFilterUpdate(editingPropertyFilter.id, updatedFilter);
            setEditingPropertyFilter(null);
            setIsPropertyFilterOpen(false);
          } : handlePropertyFilterAdd}
          existingFilter={editingPropertyFilter}
          firstTimeOnly={eventPropertyFilters.some(f => f.category === 'first_time')}
          onFirstTimeChange={handleFirstTimeChange}
        />
      )}

      {/* Global Filter Popup (predefined event + user + custom user, no custom event) */}
      {(isGlobalFilterPopupOpen || editingGlobalFilter) && (
        <EventPropertyFilterModule
          isOpen={isGlobalFilterPopupOpen || !!editingGlobalFilter}
          onClose={() => {
            setIsGlobalFilterPopupOpen(false);
            setEditingGlobalFilter(null);
          }}
          triggerRef={editingGlobalFilter && globalFilterButtonRefs.current[editingGlobalFilter.id] ? globalFilterButtonRefs.current[editingGlobalFilter.id] : addGlobalFilterRef}
          eventName={null}
          eventId={null}
          teamId={teamId}
          filters={filters}
          onFiltersChange={() => {}}
          existingFilter={editingGlobalFilter}
          firstTimeOnly={false}
          isGlobalFilter={true}
          onGlobalFilterAdd={handleGlobalFilterAdd}
        />
      )}
    </div>
  );
};

// Operator Dropdown Component
const OperatorDropdown = ({ value, operators, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  // Ensure operators is an array and has at least one element
  const safeOperators = Array.isArray(operators) && operators.length > 0 
    ? operators 
    : [{ value: 'is', label: 'Is' }]; // Default fallback operator
  
  const selectedOperator = safeOperators.find(op => op.value === value) || safeOperators[0];

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      
      setPosition({
        top: rect.bottom + scrollY + 4,
        left: rect.left + scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 cursor-pointer hover:bg-gray-200 transition-colors min-w-[100px]"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      >
        {selectedOperator.label}
      </button>

      {isOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed z-[9999] bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width || 100}px`,
              minWidth: '100px'
            }}
          >
            <div className="py-1">
              {safeOperators.map((operator) => {
                const isSelected = value === operator.value;
                return (
                  <button
                    key={operator.value}
                    type="button"
                    onClick={() => {
                      onChange(operator.value);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${
                      isSelected ? 'bg-gray-200 text-[#0f172a]' : ''
                    }`}
                  >
                    <span>{operator.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

// Property Multiselect Input Component
const PropertyMultiselectInput = ({ value = [], options = [], onChange, placeholder = "Select values...", customStyle = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const filteredOptions = options.filter(opt =>
    opt.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opt.value?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  // Calculate position when opening with viewport-aware collision handling
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 16;

      // Match dropdown width to trigger, but never smaller than 200px
      const dropdownWidth = Math.max(rect.width, 200);
      // Approximate height based on max-h-60 (≈ 15rem)
      const dropdownHeight = 240;

      // Start aligned to the left of the trigger, just below it
      let top = rect.bottom + scrollY + 4;
      let left = rect.left + scrollX;

      // Prevent overflow on the right
      if (left + dropdownWidth > viewportWidth - margin) {
        left = viewportWidth - dropdownWidth - margin;
      }

      // Prevent overflow on the left
      if (left < margin) {
        left = margin;
      }

      // If it would overflow the bottom, flip above the trigger
      if (top + dropdownHeight > viewportHeight + scrollY - margin) {
        top = rect.top + scrollY - dropdownHeight - 4;
      }

      // Ensure we keep a margin from the top
      if (top < scrollY + margin) {
        top = scrollY + margin;
      }

      setPosition({
        top,
        left,
        width: dropdownWidth,
      });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`px-2.5 py-1.5 text-xs rounded-lg focus:outline-none focus:ring-0 flex items-center justify-center min-w-[150px] ${
          customStyle 
            ? 'bg-gray-100 text-[#0f172a] hover:bg-gray-200' 
            : 'border border-gray-300 bg-white text-left focus:ring-1 focus:ring-[#1d0c46]'
        }`}
      >
        <span className={value.length === 0 ? (customStyle ? 'text-[#475569]' : 'text-[#475569]') : ''}>
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        {!customStyle && (
          <ChevronDown className={`h-3 w-3 text-[#475569] transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed z-[9999] bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width || 200}px`,
              minWidth: '200px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-[#475569]">No options found</div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOption(option.value);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
                        isSelected
                          ? 'bg-[#4b57aa] text-white hover:bg-[#4b57aa]'
                          : 'text-[#0f172a] hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className={`rounded border-gray-300 focus:ring-[#4b57aa] ${
                          isSelected ? 'text-[#4b57aa]' : ''
                        }`}
                        readOnly
                      />
                      <span>{option.label || option.value}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

// Get data type icon component
const getDataTypeIconComponent = (dataType) => {
  switch (dataType) {
    case 'string':
      return Type;
    case 'number':
      return Hash;
    case 'datetime':
      return ({ color = "currentColor", size = 18 }) => <Icon name="calendar" size={size} color={color} />;
    case 'boolean':
      return ToggleLeft;
    case 'list':
      return List;
    case 'object':
      return Box;
    default:
      return Type;
  }
};

// Get operator label
const getOperatorLabel = (operator) => {
  const operatorMap = {
    'is': 'Is',
    'is_not': 'Is not',
    'contains': 'Contains',
    'does_not_contain': 'Does not contain',
    'is_set': 'Is set',
    'is_not_set': 'Is not set',
    'equal': 'Equal',
    'not_equal': 'Not Equal',
    'greater_than': 'Greater than',
    'less_than': 'Less than',
    'is_true': 'Is true',
    'is_false': 'Is false'
  };
  return operatorMap[operator] || operator;
};

// Format filter value for display
const formatFilterValue = (filter) => {
  if (filter.operator === 'is_set' || filter.operator === 'is_not_set') {
    return '';
  }
  if (Array.isArray(filter.value)) {
    return filter.value.length === 1 ? filter.value[0] : `${filter.value.length} values`;
  }
  return filter.value || '';
};

// Data Type Selector Component (with Pencil Icon)
const DataTypeSelector = ({ filter, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const currentType = filter.propertyType || 'string';
  // For list types, show listItemType options; otherwise show property type options
  const isListType = currentType === 'list';
  const dataTypes = isListType ? [
    { value: 'string', label: 'String' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'datetime', label: 'DateTime' }
  ] : [
    { value: 'string', label: 'String' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'datetime', label: 'DateTime' },
    { value: 'list', label: 'List' }
  ];

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      
      setPosition({
        top: rect.bottom + scrollY + 4,
        left: rect.left + scrollX,
        width: Math.max(rect.width, 120)
      });
    }
  }, [isOpen]);

  const getDefaultOp = (type) => {
    switch (type) {
      case 'string': return 'is';
      case 'number': return 'equal';
      case 'boolean': return 'is_true';
      case 'datetime': return 'last';
      default: return 'is';
    }
  };

  const handleTypeChange = (newType) => {
    if (isListType) {
      // Changing listItemType
      onUpdate({ 
        listItemType: newType,
        operator: getDefaultOp(newType),
        value: null,
        value2: null
      });
    } else {
      // Changing propertyType
      onUpdate({ 
        propertyType: newType,
        operator: getDefaultOp(newType),
        value: null,
        value2: null,
        listItemType: newType === 'list' ? 'string' : null,
        listMode: newType === 'list' ? 'any_in_list' : null
      });
    }
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        title="Change data type"
      >
        <Icon name="edit_pencil" size={18} color="#475569" />
      </button>

      {isOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed z-[9999] bg-white border border-gray-300 rounded shadow-lg"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
              minWidth: '120px'
            }}
          >
            <div className="py-1">
              {dataTypes.map((type) => {
                const isSelected = isListType 
                  ? (filter.listItemType || 'string') === type.value
                  : currentType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleTypeChange(type.value)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${
                      isSelected ? 'bg-gray-200 text-[#0f172a]' : ''
                    }`}
                  >
                    <span>{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

// Event Property Filter Chip Component (Compact Display)
// Exported for reuse (e.g. Insights metrics filters)
export const EventPropertyFilterChip = ({ filter, isFirst, filterRef, onEdit, onRemove, onUpdate, teamId, eventName }) => {
  const DataTypeIcon = getDataTypeIconComponent(filter.propertyType);
  const connector = filter.connector || (isFirst ? 'WHERE' : 'AND');
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Fetch property values for "Is" and "Is Not" operators
  // For list types, check listItemType instead of propertyType
  useEffect(() => {
    const fetchPropertyValues = async () => {
      // Check if this is a string type (either direct string or list with string items)
      const isStringType = filter.propertyType === 'string' || (filter.propertyType === 'list' && (filter.listItemType || 'string') === 'string');
      const shouldFetchValues = isStringType && (filter.operator === 'is' || filter.operator === 'is_not') && filter.property && teamId;
      
      if (shouldFetchValues) {
        // First check if we have sampleValues from the API (for custom properties)
        if (filter.sampleValues && filter.sampleValues.length > 0) {
          const options = filter.sampleValues
            .map(val => ({ value: val, label: val }));
          setPropertyOptions(options);
          return;
        }

        // Predefined user properties: first/last location city/country, referrers, device/browser/os
        const predefinedUserProps = ['user.first_location_city', 'user.first_location_country', 'user.last_location_city', 'user.last_location_country', 'user.first_referrer', 'user.last_referrer', 'user.device_type', 'user.browser_name', 'user.os'];
        const isPredefinedUserProperty = filter.propertySource === 'user' && predefinedUserProps.includes(filter.property);
        if (isPredefinedUserProperty) {
          setLoadingOptions(true);
          try {
            let numericTeamId = null;
            if (typeof teamId === 'object' && teamId !== null) {
              numericTeamId = parseInt(teamId._id || teamId.id, 10);
            } else if (typeof teamId === 'string') {
              try {
                const teamDataStr = localStorage.getItem('selectedTeamData');
                if (teamDataStr) {
                  const teamData = JSON.parse(teamDataStr);
                  numericTeamId = parseInt(teamData._id || teamData.id, 10);
                } else {
                  numericTeamId = parseInt(teamId, 10);
                }
              } catch (e) {
                numericTeamId = parseInt(teamId, 10);
              }
            } else {
              numericTeamId = parseInt(teamId, 10);
            }
            if (!isNaN(numericTeamId) && numericTeamId) {
              const propertyParam = filter.property.replace(/^user\./, '');
              const optsResponse = await axiosInstance.get('/events/user-property-options', {
                params: { team_id: numericTeamId, property: propertyParam, limit: 500 }
              });
              if (optsResponse.data.success && Array.isArray(optsResponse.data.options)) {
                setPropertyOptions(optsResponse.data.options);
                setLoadingOptions(false);
                return;
              }
            }
          } catch (error) {
            console.error('Error fetching user property options:', error);
          } finally {
            setLoadingOptions(false);
          }
        }

        // Check if this is a user custom property (starts with 'user.' and propertySource is 'user')
        const isUserCustomProperty = filter.propertySource === 'user' && filter.property.startsWith('user.');
        
        if (isUserCustomProperty) {
          setLoadingOptions(true);
          try {
            let numericTeamId = null;
            if (typeof teamId === 'object' && teamId !== null) {
              numericTeamId = parseInt(teamId._id || teamId.id, 10);
            } else if (typeof teamId === 'string') {
              try {
                const teamDataStr = localStorage.getItem('selectedTeamData');
                if (teamDataStr) {
                  const teamData = JSON.parse(teamDataStr);
                  numericTeamId = parseInt(teamData._id || teamData.id, 10);
                } else {
                  numericTeamId = parseInt(teamId, 10);
                }
              } catch (e) {
                numericTeamId = parseInt(teamId, 10);
              }
            } else {
              numericTeamId = parseInt(teamId, 10);
            }

            if (isNaN(numericTeamId) || !numericTeamId) {
              setPropertyOptions([]);
              return;
            }

            // Fetch user custom properties to get sampleValues
            const userCustomPropsResponse = await axiosInstance.get('/events/user-custom-properties', {
              params: {
                team_id: numericTeamId,
                limit: 5000
              }
            });

            if (userCustomPropsResponse.data.success) {
              // Remove 'user.' prefix to find the property
              const propertyName = filter.property.replace(/^user\./, '');
              const foundProp = userCustomPropsResponse.data.custom_properties?.find(p => p.name === propertyName);

              if (foundProp && foundProp.sampleValues && foundProp.sampleValues.length > 0) {
                const options = foundProp.sampleValues
                  .map(val => ({ value: val, label: val }));
                setPropertyOptions(options);
                return;
              }
            }
          } catch (error) {
            console.error('Error fetching user custom property values:', error);
          } finally {
            setLoadingOptions(false);
          }
        }

        // For event custom properties without sampleValues, fetch from custom-properties endpoint
        const isEventCustomProperty = filter.propertySource === 'event' && !['distinct_id', 'country', 'city', 'region', 'timezone', 'browser_name', 'browser_version', 'os', 'os_version', 'device_type', 'language', 'current_url', 'page_title', 'uri', 'initial_referrer', 'referrer', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'element_name', 'element_id', 'element_class', 'element_tag_name', 'element_type', 'element_text', 'viewport_height', 'viewport_width', 'screen_height', 'screen_width', 'dpi', 'is_web3_user', 'wallet_address', 'user_identity'].includes(filter.property) && !filter.property.startsWith('auto_event_data.');
        
        // For global filters (eventName is null), try to fetch from all events by querying events directly
        if (isEventCustomProperty && !eventName) {
          setLoadingOptions(true);
          try {
            let numericTeamId = null;
            if (typeof teamId === 'object' && teamId !== null) {
              numericTeamId = parseInt(teamId._id || teamId.id, 10);
            } else if (typeof teamId === 'string') {
              try {
                const teamDataStr = localStorage.getItem('selectedTeamData');
                if (teamDataStr) {
                  const teamData = JSON.parse(teamDataStr);
                  numericTeamId = parseInt(teamData._id || teamData.id, 10);
                } else {
                  numericTeamId = parseInt(teamId, 10);
                }
              } catch (e) {
                numericTeamId = parseInt(teamId, 10);
              }
            } else {
              numericTeamId = parseInt(teamId, 10);
            }

            if (isNaN(numericTeamId) || !numericTeamId) {
              setPropertyOptions([]);
              setLoadingOptions(false);
              return;
            }

            // For global filters, fetch values from all events
            const response = await axiosInstance.get('/events', {
              params: {
                team_id: numericTeamId,
                limit: 5000
              }
            });

            const extractOptionsFromEvents = (events) => {
              const uniqueValues = new Set();
              (events || []).forEach(event => {
                const propValue = event[filter.property];
                if (propValue !== null && propValue !== undefined) {
                  const strValue = String(propValue).trim();
                  if (strValue) uniqueValues.add(strValue);
                }
              });
              return Array.from(uniqueValues).sort().map(val => ({ value: val, label: val }));
            };

            if (response.data.success && response.data.events) {
              const options = extractOptionsFromEvents(response.data.events);
              setPropertyOptions(options);
              setLoadingOptions(false);
              return;
            }
          } catch (error) {
            console.error('Error fetching custom property values for global filter:', error);
          } finally {
            setLoadingOptions(false);
          }
        }
        
        if (isEventCustomProperty && eventName) {
          setLoadingOptions(true);
          try {
            let numericTeamId = null;
            if (typeof teamId === 'object' && teamId !== null) {
              numericTeamId = parseInt(teamId._id || teamId.id, 10);
            } else if (typeof teamId === 'string') {
              try {
                const teamDataStr = localStorage.getItem('selectedTeamData');
                if (teamDataStr) {
                  const teamData = JSON.parse(teamDataStr);
                  numericTeamId = parseInt(teamData._id || teamData.id, 10);
                } else {
                  numericTeamId = parseInt(teamId, 10);
                }
              } catch (e) {
                numericTeamId = parseInt(teamId, 10);
              }
            } else {
              numericTeamId = parseInt(teamId, 10);
            }

            if (isNaN(numericTeamId) || !numericTeamId) {
              setPropertyOptions([]);
              return;
            }

            // Fetch custom properties for this specific event to get sampleValues
            const customPropsResponse = await axiosInstance.get('/events/custom-properties', {
              params: {
                team_id: numericTeamId,
                event_name: eventName,
                limit: 5000
              }
            });

            if (customPropsResponse.data.success) {
              // Find the property in custom_properties or auto_event_data
              let foundProp = null;
              if (filter.property.startsWith('auto_event_data.')) {
                const key = filter.property.replace('auto_event_data.', '');
                foundProp = customPropsResponse.data.auto_event_data?.find(p => p.name === key);
              } else {
                foundProp = customPropsResponse.data.custom_properties?.find(p => p.name === filter.property);
              }

              if (foundProp && foundProp.sampleValues && foundProp.sampleValues.length > 0) {
                const options = foundProp.sampleValues
                  .map(val => ({ value: val, label: val }));
                setPropertyOptions(options);
                return;
              }
            }
          } catch (error) {
            console.error('Error fetching custom property values:', error);
          } finally {
            setLoadingOptions(false);
          }
        }

        // For predefined properties, fetch from /events with exact event_name filter
        setLoadingOptions(true);
        try {
          let numericTeamId = null;
          if (typeof teamId === 'object' && teamId !== null) {
            numericTeamId = parseInt(teamId._id || teamId.id, 10);
          } else if (typeof teamId === 'string') {
            try {
              const teamDataStr = localStorage.getItem('selectedTeamData');
              if (teamDataStr) {
                const teamData = JSON.parse(teamDataStr);
                numericTeamId = parseInt(teamData._id || teamData.id, 10);
              } else {
                numericTeamId = parseInt(teamId, 10);
              }
            } catch (e) {
              numericTeamId = parseInt(teamId, 10);
            }
          } else {
            numericTeamId = parseInt(teamId, 10);
          }

          if (isNaN(numericTeamId) || !numericTeamId) {
            setPropertyOptions([]);
            return;
          }

          // For predefined properties: first try with event_name filter; if no options, retry without event filter
          const predefinedEventProps = ['distinct_id', 'country', 'city', 'region', 'timezone', 'browser_name', 'browser_version', 'os', 'os_version', 'device_type', 'language', 'current_url', 'page_title', 'uri', 'initial_referrer', 'referrer', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'element_name', 'element_id', 'element_class', 'element_tag_name', 'element_type', 'element_text'];
          const isPredefined = predefinedEventProps.includes(filter.property);

          const extractOptionsFromEvents = (events) => {
            const uniqueValues = new Set();
            (events || []).forEach(event => {
              const propValue = event[filter.property];
              if (propValue !== null && propValue !== undefined) {
                const strValue = String(propValue).trim();
                if (strValue) uniqueValues.add(strValue);
              }
            });
            return Array.from(uniqueValues).sort().map(val => ({ value: val, label: val }));
          };

          let filters = [];
          if (eventName) {
            filters.push({ field: 'eventName', operator: 'is', value: [eventName] });
          }

          let response = await axiosInstance.get('/events', {
            params: {
              team_id: numericTeamId,
              limit: 5000,
              ...(filters.length > 0 ? { modularFilters: encodeURIComponent(JSON.stringify(filters)) } : {})
            }
          });

          let options = response.data.success && response.data.events
            ? extractOptionsFromEvents(response.data.events)
            : [];

          if (isPredefined && options.length === 0 && eventName) {
            response = await axiosInstance.get('/events', {
              params: { team_id: numericTeamId, limit: 5000 }
            });
            if (response.data.success && response.data.events) {
              options = extractOptionsFromEvents(response.data.events);
            }
          }

          setPropertyOptions(options);
        } catch (error) {
          console.error('Error fetching property values:', error);
          setPropertyOptions([]);
        } finally {
          setLoadingOptions(false);
        }
      } else {
        setPropertyOptions([]);
      }
    };

    fetchPropertyValues();
  }, [filter.propertyType, filter.operator, filter.property, filter.sampleValues, filter.propertySource, filter.listItemType, teamId, eventName]);

  const getOperatorsForType = (dataType) => {
    switch (dataType) {
      case 'string':
        return [
          { value: 'is', label: 'Is' },
          { value: 'is_not', label: 'Is not' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
          { value: 'is_set', label: 'Is set' },
          { value: 'is_not_set', label: 'Is not set' }
        ];
      case 'number':
        return [
          { value: 'equal', label: 'Equals' },
          { value: 'not_equal', label: 'Not equal' },
          { value: 'greater_than', label: 'Greater than' },
          { value: 'greater_than_equal', label: 'Greater than or equal to' },
          { value: 'less_than', label: 'Less than' },
          { value: 'less_than_equal', label: 'Less than or equal to' },
          { value: 'between', label: 'Between' },
          { value: 'not_between', label: 'Not between' },
          { value: 'is_numeric', label: 'Is numeric' },
          { value: 'is_not_numeric', label: 'Is not numeric' }
        ];
      case 'boolean':
        return [
          { value: 'is_true', label: 'Is true' },
          { value: 'is_false', label: 'Is false' }
        ];
      case 'datetime':
      case 'date':
        return [
          { value: 'last', label: 'Last' },
          { value: 'not_in_the_last', label: 'Not in the last' },
          { value: 'between', label: 'Between' },
          { value: 'not_between', label: 'Not between' },
          { value: 'on', label: 'On' },
          { value: 'not_on', label: 'Not on' },
          { value: 'before_the_last', label: 'Before the last' },
          { value: 'before', label: 'Before' },
          { value: 'since', label: 'Since' },
          { value: 'in_the_next', label: 'In the next' }
        ];
      default:
        // Default to string operators for unknown types
        return [
          { value: 'is', label: 'Is' },
          { value: 'is_not', label: 'Is not' },
          { value: 'contains', label: 'Contains' },
          { value: 'is_set', label: 'Is set' },
          { value: 'is_not_set', label: 'Is not set' }
        ];
    }
  };

  const operators = getOperatorsForType(filter.propertyType);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-medium text-[#475569]">{connector}</span>
      <div 
        ref={filterRef}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={onEdit}
      >
        <DataTypeIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />
        <span
          className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569] flex-shrink-0"
          title={filter.propertySource === 'user' ? 'User property' : 'Event property'}
        >
          {filter.propertySource === 'user' ? 'U' : 'E'}
        </span>
        <span className="text-sm text-[#0f172a] font-medium">{filter.propertyLabel}</span>
      </div>
      {/* Operator Dropdown - For list types, show list mode first, then nested operator */}
      {filter.propertyType === 'list' ? (
        <>
          <OperatorDropdown
            value={filter.listMode || 'any_in_list'}
            operators={[
              { value: 'any_in_list', label: 'Any in list' },
              { value: 'all_in_list', label: 'All in list' }
            ]}
            onChange={(newListMode) => onUpdate({ 
              listMode: newListMode, 
              operator: getOperatorsForType(filter.listItemType || 'string')[0]?.value || 'is',
              value: null 
            })}
          />
          <OperatorDropdown
            value={filter.operator || getOperatorsForType(filter.listItemType || 'string')[0]?.value || 'is'}
            operators={operators}
            onChange={(newOperator) => onUpdate({ operator: newOperator, value: null })}
          />
        </>
      ) : (
        <OperatorDropdown
          value={filter.operator}
          operators={operators}
          onChange={(newOperator) => onUpdate({ operator: newOperator, value: null })}
        />
      )}
      {/* Value Input - Separate */}
      {filter.propertyType === 'string' && (filter.operator === 'is' || filter.operator === 'is_not') ? (
        <PropertyMultiselectInput
          value={Array.isArray(filter.value) ? filter.value : (filter.value ? [filter.value] : [])}
          options={propertyOptions}
          onChange={(newValue) => onUpdate({ value: newValue })}
          placeholder={loadingOptions ? "Loading values..." : "Select values..."}
          customStyle={true}
        />
      ) : filter.propertyType === 'string' && (filter.operator === 'contains' || filter.operator === 'does_not_contain') ? (
        <input
          type="text"
          value={filter.value || ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          placeholder="Enter value..."
          className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-24"
          onClick={(e) => e.stopPropagation()}
        />
      ) : filter.propertyType === 'number' && (filter.operator === 'between' || filter.operator === 'not_between') ? (
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder="From..."
            className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
            onClick={(e) => e.stopPropagation()}
          />
          <span className="text-xs text-[#475569]">to</span>
          <input
            type="number"
            value={filter.value2 || ''}
            onChange={(e) => onUpdate({ value2: e.target.value })}
            placeholder="To..."
            className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : filter.propertyType === 'number' && (filter.operator === 'is_numeric' || filter.operator === 'is_not_numeric') ? null : (
        filter.propertyType === 'datetime' && (filter.operator === 'last' || filter.operator === 'not_in_the_last') ? (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="X"
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-16"
              onClick={(e) => e.stopPropagation()}
            />
            <select
              value={filter.value2 || 'hours'}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
              <option value="years">years</option>
            </select>
            {filter.operator === 'not_in_the_last' && (
              <span className="text-xs text-[#475569]">ago</span>
            )}
          </div>
        ) : filter.propertyType === 'datetime' && (filter.operator === 'between' || filter.operator === 'not_between') ? (
          <div className="flex flex-col gap-1 w-full max-w-[200px]">
            <input
              type="datetime-local"
              value={filter.value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              className="w-full max-w-[200px] px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="text-xs text-[#475569] text-center">to</span>
            <input
              type="datetime-local"
              value={filter.value2 || ''}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="w-full max-w-[200px] px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ) : filter.propertyType === 'datetime' && (filter.operator === 'on' || filter.operator === 'not_on' || filter.operator === 'before' || filter.operator === 'since' || filter.operator === 'in_the_next') ? (
          <input
            type="datetime-local"
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
            onClick={(e) => e.stopPropagation()}
          />
        ) : filter.propertyType === 'datetime' && filter.operator === 'before_the_last' ? (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={filter.value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="X"
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-16"
              onClick={(e) => e.stopPropagation()}
            />
            <select
              value={filter.value2 || 'hours'}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
              <option value="years">years</option>
            </select>
            <span className="text-xs text-[#475569]">ago</span>
          </div>
        ) : filter.propertyType === 'list' ? (
        // List type value input - based on listItemType
        (() => {
          const listItemType = filter.listItemType || 'string';
          const listOperator = filter.operator || 'is';
          
          if (listItemType === 'string') {
            if (listOperator === 'is_set' || listOperator === 'is_not_set') {
              return null;
            }
            if (listOperator === 'is' || listOperator === 'is_not') {
              // Use propertyOptions if available (from useEffect), otherwise fallback to sampleValues
              const options = propertyOptions.length > 0 
                ? propertyOptions 
                : (filter.sampleValues && filter.sampleValues.length > 0 
                    ? filter.sampleValues.map(val => ({ value: val, label: val }))
                    : []);
              
              if (options.length > 0) {
                return (
                  <PropertyMultiselectInput
                    value={Array.isArray(filter.value) ? filter.value : (filter.value ? [filter.value] : [])}
                    options={options}
                    onChange={(newValue) => onUpdate({ value: newValue })}
                    placeholder={loadingOptions ? "Loading values..." : "Select values..."}
                    customStyle={true}
                  />
                );
              }
              // Fallback to text input if no options
              return (
                <input
                  type="text"
                  value={filter.value || ''}
                  onChange={(e) => onUpdate({ value: e.target.value })}
                  placeholder="Enter value..."
                  className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-24"
                  onClick={(e) => e.stopPropagation()}
                />
              );
            }
            return (
              <input
                type="text"
                value={filter.value || ''}
                onChange={(e) => onUpdate({ value: e.target.value })}
                placeholder="Enter value..."
                className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-24"
                onClick={(e) => e.stopPropagation()}
              />
            );
          }
          
          if (listItemType === 'number') {
            if (listOperator === 'is_numeric' || listOperator === 'is_not_numeric') {
              return null;
            }
            if (listOperator === 'between' || listOperator === 'not_between') {
              return (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={filter.value || ''}
                    onChange={(e) => onUpdate({ value: e.target.value })}
                    placeholder="From..."
                    className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="text-xs text-[#475569]">to</span>
                  <input
                    type="number"
                    value={filter.value2 || ''}
                    onChange={(e) => onUpdate({ value2: e.target.value })}
                    placeholder="To..."
                    className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              );
            }
            return (
              <input
                type="number"
                value={filter.value || ''}
                onChange={(e) => onUpdate({ value: e.target.value })}
                placeholder="Enter value..."
                className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-24"
                onClick={(e) => e.stopPropagation()}
              />
            );
          }
          
          if (listItemType === 'boolean') {
            return null; // No value needed
          }
          
          if (listItemType === 'datetime') {
            if (listOperator === 'between' || listOperator === 'not_between') {
              return (
                <div className="flex items-center gap-1">
                  <input
                    type="datetime-local"
                    value={filter.value || ''}
                    onChange={(e) => onUpdate({ value: e.target.value })}
                    className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="text-xs text-[#475569]">to</span>
                  <input
                    type="datetime-local"
                    value={filter.value2 || ''}
                    onChange={(e) => onUpdate({ value2: e.target.value })}
                    className="px-2 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              );
            }
            return (
              <input
                type="datetime-local"
                value={filter.value || ''}
                onChange={(e) => onUpdate({ value: e.target.value })}
                className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors"
                onClick={(e) => e.stopPropagation()}
              />
            );
          }
          
          // Default to string input
          return (
            <input
              type="text"
              value={filter.value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="Enter value..."
              className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-24"
              onClick={(e) => e.stopPropagation()}
            />
          );
        })()
      ) : filter.operator !== 'is_set' && filter.operator !== 'is_not_set' && 
            !(filter.propertyType === 'boolean' && (filter.operator === 'is_true' || filter.operator === 'is_false')) && (
          <input
            type={filter.propertyType === 'number' ? 'number' : 'text'}
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder="Enter value..."
            className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-24"
            onClick={(e) => e.stopPropagation()}
          />
        )
      )}
      {/* Data Type Selector with Pencil Icon */}
      <DataTypeSelector filter={filter} onUpdate={onUpdate} />
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        title="Delete"
      >
        <Icon name="delete" size={16} color="#475569" />
      </button>
    </div>
  );
};

// Event Property Filter Editor Component (Editable Form)
const EventPropertyFilterEditor = ({ filter, isFirst, onUpdate, onRemove, onCancel, teamId, eventName }) => {
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const DataTypeIcon = getDataTypeIconComponent(filter.propertyType);

  const getOperatorsForType = (dataType) => {
    switch (dataType) {
      case 'string':
        return [
          { value: 'is', label: 'Is' },
          { value: 'is_not', label: 'Is not' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
          { value: 'is_set', label: 'Is set' },
          { value: 'is_not_set', label: 'Is not set' }
        ];
      case 'number':
        return [
          { value: 'equal', label: 'Equals' },
          { value: 'not_equal', label: 'Not equal' },
          { value: 'greater_than', label: 'Greater than' },
          { value: 'greater_than_equal', label: 'Greater than or equal to' },
          { value: 'less_than', label: 'Less than' },
          { value: 'less_than_equal', label: 'Less than or equal to' },
          { value: 'between', label: 'Between' },
          { value: 'not_between', label: 'Not between' },
          { value: 'is_numeric', label: 'Is numeric' },
          { value: 'is_not_numeric', label: 'Is not numeric' }
        ];
      case 'boolean':
        return [
          { value: 'is_true', label: 'Is true' },
          { value: 'is_false', label: 'Is false' }
        ];
      case 'datetime':
      case 'date':
        return [
          { value: 'last', label: 'Last' },
          { value: 'not_in_the_last', label: 'Not in the last' },
          { value: 'between', label: 'Between' },
          { value: 'not_between', label: 'Not between' },
          { value: 'on', label: 'On' },
          { value: 'not_on', label: 'Not on' },
          { value: 'before_the_last', label: 'Before the last' },
          { value: 'before', label: 'Before' },
          { value: 'since', label: 'Since' },
          { value: 'in_the_next', label: 'In the next' }
        ];
      default:
        // Default to string operators for unknown types
        return [
          { value: 'is', label: 'Is' },
          { value: 'is_not', label: 'Is not' },
          { value: 'contains', label: 'Contains' },
          { value: 'is_set', label: 'Is set' },
          { value: 'is_not_set', label: 'Is not set' }
        ];
    }
  };

  // For list types, operators are based on listItemType
  const operators = filter.propertyType === 'list' 
    ? getOperatorsForType(filter.listItemType || 'string')
    : getOperatorsForType(filter.propertyType);

  // Fetch property values for "Is" and "Is Not" operators
  // For list types, check listItemType instead of propertyType
  useEffect(() => {
    const fetchPropertyValues = async () => {
      // Check if this is a string type (either direct string or list with string items)
      const isStringType = filter.propertyType === 'string' || (filter.propertyType === 'list' && (filter.listItemType || 'string') === 'string');
      const shouldFetchValues = isStringType && (filter.operator === 'is' || filter.operator === 'is_not') && filter.property && teamId;
      
      if (shouldFetchValues) {
        // First check if we have sampleValues from the API (for custom properties)
        if (filter.sampleValues && filter.sampleValues.length > 0) {
          const options = filter.sampleValues
            .map(val => ({ value: val, label: val }));
          setPropertyOptions(options);
          return;
        }

        // Predefined user properties: first/last location city/country, referrers, device/browser/os
        const predefinedUserPropsList = ['user.first_location_city', 'user.first_location_country', 'user.last_location_city', 'user.last_location_country', 'user.first_referrer', 'user.last_referrer', 'user.device_type', 'user.browser_name', 'user.os'];
        const isPredefinedUserProp = filter.propertySource === 'user' && predefinedUserPropsList.includes(filter.property);
        if (isPredefinedUserProp) {
          setLoadingOptions(true);
          try {
            let numericTeamId = null;
            if (typeof teamId === 'object' && teamId !== null) {
              numericTeamId = parseInt(teamId._id || teamId.id, 10);
            } else if (typeof teamId === 'string') {
              try {
                const teamDataStr = localStorage.getItem('selectedTeamData');
                if (teamDataStr) {
                  const teamData = JSON.parse(teamDataStr);
                  numericTeamId = parseInt(teamData._id || teamData.id, 10);
                } else {
                  numericTeamId = parseInt(teamId, 10);
                }
              } catch (e) {
                numericTeamId = parseInt(teamId, 10);
              }
            } else {
              numericTeamId = parseInt(teamId, 10);
            }
            if (!isNaN(numericTeamId) && numericTeamId) {
              const propertyParam = filter.property.replace(/^user\./, '');
              const optsResponse = await axiosInstance.get('/events/user-property-options', {
                params: { team_id: numericTeamId, property: propertyParam, limit: 500 }
              });
              if (optsResponse.data.success && Array.isArray(optsResponse.data.options)) {
                setPropertyOptions(optsResponse.data.options);
                setLoadingOptions(false);
                return;
              }
            }
          } catch (error) {
            console.error('Error fetching user property options:', error);
          } finally {
            setLoadingOptions(false);
          }
        }

        // Check if this is a user custom property (starts with 'user.' and propertySource is 'user')
        const isUserCustomProperty = filter.propertySource === 'user' && filter.property.startsWith('user.');
        
        if (isUserCustomProperty) {
          setLoadingOptions(true);
          try {
            let numericTeamId = null;
            if (typeof teamId === 'object' && teamId !== null) {
              numericTeamId = parseInt(teamId._id || teamId.id, 10);
            } else if (typeof teamId === 'string') {
              try {
                const teamDataStr = localStorage.getItem('selectedTeamData');
                if (teamDataStr) {
                  const teamData = JSON.parse(teamDataStr);
                  numericTeamId = parseInt(teamData._id || teamData.id, 10);
                } else {
                  numericTeamId = parseInt(teamId, 10);
                }
              } catch (e) {
                numericTeamId = parseInt(teamId, 10);
              }
            } else {
              numericTeamId = parseInt(teamId, 10);
            }

            if (isNaN(numericTeamId) || !numericTeamId) {
              setPropertyOptions([]);
              return;
            }

            // Fetch user custom properties to get sampleValues
            const userCustomPropsResponse = await axiosInstance.get('/events/user-custom-properties', {
              params: {
                team_id: numericTeamId,
                limit: 5000
              }
            });

            if (userCustomPropsResponse.data.success) {
              // Remove 'user.' prefix to find the property
              const propertyName = filter.property.replace(/^user\./, '');
              const foundProp = userCustomPropsResponse.data.custom_properties?.find(p => p.name === propertyName);

              if (foundProp && foundProp.sampleValues && foundProp.sampleValues.length > 0) {
                const options = foundProp.sampleValues
                  .map(val => ({ value: val, label: val }));
                setPropertyOptions(options);
                return;
              }
            }
          } catch (error) {
            console.error('Error fetching user custom property values:', error);
          } finally {
            setLoadingOptions(false);
          }
        }

        // For event custom properties without sampleValues, fetch from custom-properties endpoint
        const isEventCustomProperty = filter.propertySource === 'event' && !['distinct_id', 'country', 'city', 'region', 'timezone', 'browser_name', 'browser_version', 'os', 'os_version', 'device_type', 'language', 'current_url', 'page_title', 'uri', 'initial_referrer', 'referrer', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'element_name', 'element_id', 'element_class', 'element_tag_name', 'element_type', 'element_text', 'viewport_height', 'viewport_width', 'screen_height', 'screen_width', 'dpi', 'is_web3_user', 'wallet_address', 'user_identity'].includes(filter.property) && !filter.property.startsWith('auto_event_data.');
        
        // For global filters (eventName is null), try to fetch from all events by querying events directly
        if (isEventCustomProperty && !eventName) {
          setLoadingOptions(true);
          try {
            let numericTeamId = null;
            if (typeof teamId === 'object' && teamId !== null) {
              numericTeamId = parseInt(teamId._id || teamId.id, 10);
            } else if (typeof teamId === 'string') {
              try {
                const teamDataStr = localStorage.getItem('selectedTeamData');
                if (teamDataStr) {
                  const teamData = JSON.parse(teamDataStr);
                  numericTeamId = parseInt(teamData._id || teamData.id, 10);
                } else {
                  numericTeamId = parseInt(teamId, 10);
                }
              } catch (e) {
                numericTeamId = parseInt(teamId, 10);
              }
            } else {
              numericTeamId = parseInt(teamId, 10);
            }

            if (isNaN(numericTeamId) || !numericTeamId) {
              setPropertyOptions([]);
              setLoadingOptions(false);
              return;
            }

            // For global filters, fetch values from all events
            const response = await axiosInstance.get('/events', {
              params: {
                team_id: numericTeamId,
                limit: 5000
              }
            });

            const extractOptionsFromEvents = (events) => {
              const uniqueValues = new Set();
              (events || []).forEach(event => {
                const propValue = event[filter.property];
                if (propValue !== null && propValue !== undefined) {
                  const strValue = String(propValue).trim();
                  if (strValue) uniqueValues.add(strValue);
                }
              });
              return Array.from(uniqueValues).sort().map(val => ({ value: val, label: val }));
            };

            if (response.data.success && response.data.events) {
              const options = extractOptionsFromEvents(response.data.events);
              setPropertyOptions(options);
              setLoadingOptions(false);
              return;
            }
          } catch (error) {
            console.error('Error fetching custom property values for global filter:', error);
          } finally {
            setLoadingOptions(false);
          }
        }
        
        if (isEventCustomProperty && eventName) {
          setLoadingOptions(true);
          try {
            let numericTeamId = null;
            if (typeof teamId === 'object' && teamId !== null) {
              numericTeamId = parseInt(teamId._id || teamId.id, 10);
            } else if (typeof teamId === 'string') {
              try {
                const teamDataStr = localStorage.getItem('selectedTeamData');
                if (teamDataStr) {
                  const teamData = JSON.parse(teamDataStr);
                  numericTeamId = parseInt(teamData._id || teamData.id, 10);
                } else {
                  numericTeamId = parseInt(teamId, 10);
                }
              } catch (e) {
                numericTeamId = parseInt(teamId, 10);
              }
            } else {
              numericTeamId = parseInt(teamId, 10);
            }

            if (isNaN(numericTeamId) || !numericTeamId) {
              setPropertyOptions([]);
              return;
            }

            // Fetch custom properties for this specific event to get sampleValues
            const customPropsResponse = await axiosInstance.get('/events/custom-properties', {
              params: {
                team_id: numericTeamId,
                event_name: eventName,
                limit: 5000
              }
            });

            if (customPropsResponse.data.success) {
              // Find the property in custom_properties or auto_event_data
              let foundProp = null;
              if (filter.property.startsWith('auto_event_data.')) {
                const key = filter.property.replace('auto_event_data.', '');
                foundProp = customPropsResponse.data.auto_event_data?.find(p => p.name === key);
              } else {
                foundProp = customPropsResponse.data.custom_properties?.find(p => p.name === filter.property);
              }

              if (foundProp && foundProp.sampleValues && foundProp.sampleValues.length > 0) {
                const options = foundProp.sampleValues
                  .map(val => ({ value: val, label: val }));
                setPropertyOptions(options);
                return;
              }
            }
          } catch (error) {
            console.error('Error fetching custom property values:', error);
          } finally {
            setLoadingOptions(false);
          }
        }

        // For predefined properties, fetch from /events with exact event_name filter
        setLoadingOptions(true);
        try {
          let numericTeamId = null;
          if (typeof teamId === 'object' && teamId !== null) {
            numericTeamId = parseInt(teamId._id || teamId.id, 10);
          } else if (typeof teamId === 'string') {
            try {
              const teamDataStr = localStorage.getItem('selectedTeamData');
              if (teamDataStr) {
                const teamData = JSON.parse(teamDataStr);
                numericTeamId = parseInt(teamData._id || teamData.id, 10);
              } else {
                numericTeamId = parseInt(teamId, 10);
              }
            } catch (e) {
              numericTeamId = parseInt(teamId, 10);
            }
          } else {
            numericTeamId = parseInt(teamId, 10);
          }

          if (isNaN(numericTeamId) || !numericTeamId) {
            setPropertyOptions([]);
            return;
          }

          // For predefined properties: first try with event_name filter; if no options, retry without event filter
          const predefinedEventProps = ['distinct_id', 'country', 'city', 'region', 'timezone', 'browser_name', 'browser_version', 'os', 'os_version', 'device_type', 'language', 'current_url', 'page_title', 'uri', 'initial_referrer', 'referrer', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'element_name', 'element_id', 'element_class', 'element_tag_name', 'element_type', 'element_text'];
          const isPredefined = predefinedEventProps.includes(filter.property);

          const extractOptionsFromEvents = (events) => {
            const uniqueValues = new Set();
            (events || []).forEach(event => {
              const propValue = event[filter.property];
              if (propValue !== null && propValue !== undefined) {
                const strValue = String(propValue).trim();
                if (strValue) uniqueValues.add(strValue);
              }
            });
            return Array.from(uniqueValues).sort().map(val => ({ value: val, label: val }));
          };

          let filters = [];
          if (eventName) {
            filters.push({ field: 'eventName', operator: 'is', value: [eventName] });
          }

          let response = await axiosInstance.get('/events', {
            params: {
              team_id: numericTeamId,
              limit: 5000,
              ...(filters.length > 0 ? { modularFilters: encodeURIComponent(JSON.stringify(filters)) } : {})
            }
          });

          let options = response.data.success && response.data.events
            ? extractOptionsFromEvents(response.data.events)
            : [];

          if (isPredefined && options.length === 0 && eventName) {
            response = await axiosInstance.get('/events', {
              params: { team_id: numericTeamId, limit: 5000 }
            });
            if (response.data.success && response.data.events) {
              options = extractOptionsFromEvents(response.data.events);
            }
          }

          setPropertyOptions(options);
        } catch (error) {
          console.error('Error fetching property values:', error);
          setPropertyOptions([]);
        } finally {
          setLoadingOptions(false);
        }
      } else {
        setPropertyOptions([]);
      }
    };

    fetchPropertyValues();
  }, [filter.propertyType, filter.operator, filter.property, filter.sampleValues, filter.propertySource, filter.listItemType, teamId, eventName]);

  return (
    <div className="flex items-center gap-2 flex-wrap text-sm">
      {/* Connector (WHERE/AND/OR) */}
      {isFirst ? (
        <span className="font-medium text-[#475569]">WHERE</span>
      ) : (
        <select
          value={filter.connector}
          onChange={(e) => onUpdate({ connector: e.target.value })}
          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      )}

      {/* Data Type Icon */}
      <DataTypeIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />

      {/* Property Source Tag: E = Event, U = User */}
      <span
        className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569] flex-shrink-0"
        title={filter.propertySource === 'user' ? 'User property' : 'Event property'}
      >
        {filter.propertySource === 'user' ? 'U' : 'E'}
      </span>

      {/* Property Name */}
      <span className="font-medium text-[#0f172a]">{filter.propertyLabel}</span>

      {/* Operator - For list types, show list mode first, then nested operator */}
      {filter.propertyType === 'list' ? (
        <>
          {/* First: List Mode (Any in list / All in list) */}
          <select
            value={filter.listMode || 'any_in_list'}
            onChange={(e) => {
              const listMode = e.target.value;
              onUpdate({ 
                listMode: listMode,
                operator: getOperatorsForType(filter.listItemType || 'string')[0]?.value || 'is',
                value: null,
                value2: null
              });
            }}
            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
          >
            <option value="any_in_list">Any in list</option>
            <option value="all_in_list">All in list</option>
          </select>
          {/* Second: Nested Operator */}
          <select
            value={filter.operator || getOperatorsForType(filter.listItemType || 'string')[0]?.value || 'is'}
            onChange={(e) => onUpdate({ operator: e.target.value, value: null, value2: null })}
            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
          >
            {operators.map(op => (
              <option key={op.value} value={op.value}>{op.label}</option>
            ))}
          </select>
        </>
      ) : (
        <select
          value={filter.operator}
          onChange={(e) => onUpdate({ operator: e.target.value, value: null })}
          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
        >
          {operators.map(op => (
            <option key={op.value} value={op.value}>{op.label}</option>
          ))}
        </select>
      )}

      {/* Value Input */}
      {filter.propertyType === 'list' ? (
        // List type value input - based on listItemType
        (() => {
          const listItemType = filter.listItemType || 'string';
          const listOperator = filter.operator || 'is';
          
          if (listItemType === 'string') {
            if (listOperator === 'is_set' || listOperator === 'is_not_set') {
              return null;
            }
            if (listOperator === 'is' || listOperator === 'is_not') {
              // Use propertyOptions if available (from useEffect), otherwise fallback to sampleValues
              const options = propertyOptions.length > 0 
                ? propertyOptions 
                : (filter.sampleValues && filter.sampleValues.length > 0 
                    ? filter.sampleValues.map(val => ({ value: val, label: val }))
                    : []);
              
              if (options.length > 0) {
                return (
                  <PropertyMultiselectInput
                    value={Array.isArray(filter.value) ? filter.value : (filter.value ? [filter.value] : [])}
                    options={options}
                    onChange={(newValue) => onUpdate({ value: newValue })}
                    placeholder={loadingOptions ? "Loading values..." : "Select values..."}
                  />
                );
              }
              // Fallback to text input if no options
              return (
                <input
                  type="text"
                  value={filter.value || ''}
                  onChange={(e) => onUpdate({ value: e.target.value })}
                  placeholder="Enter value..."
                  className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-32"
                />
              );
            }
            return (
              <input
                type="text"
                value={filter.value || ''}
                onChange={(e) => onUpdate({ value: e.target.value })}
                placeholder="Enter value..."
                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-32"
              />
            );
          }
          
          if (listItemType === 'number') {
            if (listOperator === 'is_numeric' || listOperator === 'is_not_numeric') {
              return null;
            }
            if (listOperator === 'between' || listOperator === 'not_between') {
              return (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={filter.value || ''}
                    onChange={(e) => onUpdate({ value: e.target.value })}
                    placeholder="From..."
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-20"
                  />
                  <span className="text-xs text-[#475569]">to</span>
                  <input
                    type="number"
                    value={filter.value2 || ''}
                    onChange={(e) => onUpdate({ value2: e.target.value })}
                    placeholder="To..."
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-20"
                  />
                </div>
              );
            }
            return (
              <input
                type="number"
                value={filter.value || ''}
                onChange={(e) => onUpdate({ value: e.target.value })}
                placeholder="Enter value..."
                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-32"
              />
            );
          }
          
          if (listItemType === 'boolean') {
            return null; // No value needed
          }
          
          if (listItemType === 'datetime') {
            if (listOperator === 'between' || listOperator === 'not_between') {
              return (
                <div className="flex items-center gap-2">
                  <input
                    type="datetime-local"
                    value={filter.value || ''}
                    onChange={(e) => onUpdate({ value: e.target.value })}
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
                  />
                  <span className="text-xs text-[#475569]">to</span>
                  <input
                    type="datetime-local"
                    value={filter.value2 || ''}
                    onChange={(e) => onUpdate({ value2: e.target.value })}
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
                  />
                </div>
              );
            }
            return (
              <input
                type="datetime-local"
                value={filter.value || ''}
                onChange={(e) => onUpdate({ value: e.target.value })}
                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
              />
            );
          }
          
          // Default to string input
          return (
            <input
              type="text"
              value={filter.value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="Enter value..."
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-32"
            />
          );
        })()
      ) : filter.propertyType === 'string' && (filter.operator === 'is' || filter.operator === 'is_not') ? (
        <PropertyMultiselectInput
          value={Array.isArray(filter.value) ? filter.value : (filter.value ? [filter.value] : [])}
          options={propertyOptions}
          onChange={(newValue) => onUpdate({ value: newValue })}
          placeholder={loadingOptions ? "Loading values..." : "Select values..."}
        />
      ) : filter.propertyType === 'string' && (filter.operator === 'contains' || filter.operator === 'does_not_contain') ? (
        <input
          type="text"
          value={filter.value || ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          placeholder="Enter value..."
          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-24"
        />
      ) : filter.propertyType === 'number' && (filter.operator === 'between' || filter.operator === 'not_between') ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder="From..."
            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-20"
          />
          <span className="text-xs text-[#475569]">to</span>
          <input
            type="number"
            value={filter.value2 || ''}
            onChange={(e) => onUpdate({ value2: e.target.value })}
            placeholder="To..."
            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-20"
          />
        </div>
      ) : filter.propertyType === 'number' && (filter.operator === 'is_numeric' || filter.operator === 'is_not_numeric') ? null : (
        filter.propertyType === 'datetime' && (filter.operator === 'last' || filter.operator === 'not_in_the_last') ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={filter.value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="X"
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-16"
            />
            <select
              value={filter.value2 || 'hours'}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
              <option value="years">years</option>
            </select>
            {filter.operator === 'not_in_the_last' && (
              <span className="text-xs text-[#475569]">ago</span>
            )}
          </div>
        ) : filter.propertyType === 'datetime' && (filter.operator === 'between' || filter.operator === 'not_between') ? (
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              value={filter.value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
            />
            <span className="text-xs text-[#475569]">to</span>
            <input
              type="datetime-local"
              value={filter.value2 || ''}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
            />
          </div>
        ) : filter.propertyType === 'datetime' && (filter.operator === 'on' || filter.operator === 'not_on' || filter.operator === 'before' || filter.operator === 'since' || filter.operator === 'in_the_next') ? (
          <input
            type="datetime-local"
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
          />
        ) : filter.propertyType === 'datetime' && filter.operator === 'before_the_last' ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={filter.value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="X"
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-16"
            />
            <select
              value={filter.value2 || 'hours'}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46]"
            >
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
              <option value="years">years</option>
            </select>
            <span className="text-xs text-[#475569]">ago</span>
          </div>
        ) : filter.operator !== 'is_set' && filter.operator !== 'is_not_set' && 
            !(filter.propertyType === 'boolean' && (filter.operator === 'is_true' || filter.operator === 'is_false')) && (
          <input
            type={filter.propertyType === 'number' ? 'number' : 'text'}
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder="Enter value..."
            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-[#1d0c46] w-24"
          />
        )
      )}

      {/* Data Type Selector with Pencil Icon */}
      <DataTypeSelector filter={filter} onUpdate={onUpdate} />

      {/* Cancel Button */}
      <button
        onClick={onCancel}
        className="px-2 py-1 text-xs text-[#475569] hover:bg-gray-100 rounded transition-colors"
        title="Cancel"
      >
        Cancel
      </button>

      {/* Delete Button */}
      <button
        onClick={onRemove}
        className="p-1 text-[#475569] hover:text-red-600 transition-colors"
        title="Delete filter"
      >
        <Icon name="delete" size={12} />
      </button>
    </div>
  );
};

export default EventsFilterModule;
